import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  FileText,
  Star,
  Clock,
  Upload,
  Eye,
  Check,
  Plus,
  AlertCircle,
  Loader,
  X
} from 'lucide-react'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface Resume {
  uuid: string
  original_filename: string
  file_size: number
  file_type: string
  is_primary: boolean
  upload_status: string
  created_at: string
  updated_at: string
}

interface ResumeSelectorProps {
  selectedResumeId?: string
  onResumeSelect: (resumeId: string | null, resumeUrl?: string) => void
  onError?: (error: string) => void
  className?: string
  showMetadata?: boolean
}

export default function ResumeSelector({ 
  selectedResumeId, 
  onResumeSelect, 
  onError,
  className = '',
  showMetadata = true 
}: ResumeSelectorProps) {
  const [resumes, setResumes] = useState<Resume[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)

  useEffect(() => {
    loadResumes()
  }, [])

  const loadResumes = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(buildApiUrl('file-uploads/resumes'), {
        method: 'GET',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Failed to load resumes: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && data.resumes) {
        setResumes(data.resumes)
        
        // Auto-select primary resume if no resume is selected
        if (!selectedResumeId && data.resumes.length > 0) {
          const primaryResume = data.resumes.find((resume: Resume) => resume.is_primary)
          if (primaryResume) {
            onResumeSelect(primaryResume.uuid, await getResumeDownloadUrl(primaryResume.uuid))
          }
        }
      } else {
        throw new Error(data.error || 'Failed to load resumes')
      }
    } catch (err) {
      console.error('Error loading resumes:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load resumes'
      setError(errorMessage)
      onError?.(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const getResumeDownloadUrl = async (resumeUuid: string): Promise<string | undefined> => {
    try {
      const response = await fetch(buildApiUrl(`file-uploads/resume/${resumeUuid}/download`), {
        method: 'GET',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })

      if (!response.ok) {
        throw new Error(`Failed to get download URL: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      
      if (data.success && data.download_url) {
        return data.download_url
      }
    } catch (err) {
      console.error('Error getting download URL:', err)
    }
    
    return undefined
  }

  const handleResumeSelect = async (resume: Resume) => {
    const downloadUrl = await getResumeDownloadUrl(resume.uuid)
    onResumeSelect(resume.uuid, downloadUrl)
  }

  const handleUploadNewResume = async (file: File) => {
    try {
      setUploading(true)
      setUploadProgress(0)
      setError(null)

      console.log('🔄 Starting resume upload for application:', file.name, file.size, 'bytes')

      // Step 1: Get presigned URL
      const presignedResponse = await fetch(buildApiUrl('file-uploads/resume/presigned-url'), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type
        })
      })
      
      if (!presignedResponse.ok) {
        throw new Error(`Presigned URL request failed: ${presignedResponse.status} ${presignedResponse.statusText}`)
      }
      
      const presignedData = await presignedResponse.json()

      if (!presignedData || !presignedData.success) {
        throw new Error(presignedData?.error || 'Failed to get upload URL')
      }

      const { upload_url, fields, s3_key } = presignedData
      
      if (!upload_url || !fields || !s3_key) {
        throw new Error('Invalid presigned URL response')
      }

      setUploadProgress(25)

      // Step 2: Upload to S3
      const formData = new FormData()
      
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string)
      })
      
      formData.append('file', file)

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      })

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        console.error('❌ S3 upload failed:', errorText)
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      setUploadProgress(75)

      // Step 3: Confirm upload and create candidate_resume record
      const confirmBody = {
        s3_key: s3_key,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type
      }
      
      const confirmResponse = await fetch(buildApiUrl('file-uploads/resume/confirm-upload'), {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(confirmBody),
        mode: 'cors',
        credentials: 'omit'
      })
      
      if (!confirmResponse.ok) {
        throw new Error(`Confirmation request failed: ${confirmResponse.status} ${confirmResponse.statusText}`)
      }
      
      const confirmData = await confirmResponse.json()

      if (!confirmData || !confirmData.success) {
        throw new Error(confirmData?.error || 'Failed to confirm upload')
      }

      setUploadProgress(100)
      console.log('🎉 Resume upload complete!')
      
      // Reload resumes list and select the new resume
      await loadResumes()
      
      // Auto-select the newly uploaded resume
      setTimeout(async () => {
        const newResumeUuid = confirmData.resume_uuid
        if (newResumeUuid) {
          const downloadUrl = await getResumeDownloadUrl(newResumeUuid)
          onResumeSelect(newResumeUuid, downloadUrl)
        }
      }, 500)

      setShowUploadModal(false)

    } catch (error) {
      console.error('❌ Resume upload error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      setError(errorMessage)
      onError?.(errorMessage)
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
            <p className="text-gray-600 text-sm">Loading resumes...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Select Resume</h3>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4 mr-1" />
              Upload New
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Choose from your existing resumes or upload a new one</p>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {resumes.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No resumes found</h3>
            <p className="text-gray-600 mb-4">Upload your first resume to get started</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Resume
            </button>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {resumes.map((resume, index) => (
              <motion.div
                key={resume.uuid}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedResumeId === resume.uuid ? 'bg-blue-50 border-blue-200' : ''
                }`}
                onClick={() => handleResumeSelect(resume)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedResumeId === resume.uuid ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <FileText className={`w-5 h-5 ${
                        selectedResumeId === resume.uuid ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900 truncate">{resume.original_filename}</h4>
                        {resume.is_primary && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Star className="w-3 h-3 mr-1" />
                            Primary
                          </span>
                        )}
                        {selectedResumeId === resume.uuid && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Check className="w-3 h-3 mr-1" />
                            Selected
                          </span>
                        )}
                      </div>
                      
                      {showMetadata && (
                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                          <span>{formatFileSize(resume.file_size)}</span>
                          <span>•</span>
                          <span>{resume.file_type.toUpperCase()}</span>
                          <span>•</span>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Uploaded {formatDate(resume.created_at)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        const downloadUrl = await getResumeDownloadUrl(resume.uuid)
                        if (downloadUrl) {
                          window.open(downloadUrl, '_blank')
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Preview resume"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Upload New Resume</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                disabled={uploading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {uploading ? (
                <div className="space-y-4 text-center">
                  <Loader className="w-12 h-12 animate-spin mx-auto text-blue-600" />
                  <div>
                    <p className="text-gray-600 mb-2">Uploading resume...</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{uploadProgress}%</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">Upload Resume</p>
                    <p className="text-gray-600 mb-4">Support for PDF, DOC, and DOCX files up to 5MB</p>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          handleUploadNewResume(file)
                        }
                      }}
                      className="hidden"
                      id="resume-upload"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg cursor-pointer transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Choose File
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Your resume will be added to your account and can be used for future applications
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
